/**
 * The planning the user is working in, loaded once into the planning store.
 * Pages and planning views render inside <ActivePlanningGate>.
 */
import { useEffect, useRef, type ReactNode } from 'react';
import { Skeleton } from '../components/Spinner.js';
import { ErrorState } from '../components/States.js';
import { NoPlanning } from '../components/NoPlanning.js';
import { planningStore, usePlanningStore } from '../store/planningStore.js';
import { planningIdOf, useMe } from './session.js';
import styles from './activePlanning.module.css';

export interface ActivePlanning {
  planningId: string | null;
  status: 'idle' | 'loading' | 'ready' | 'error';
  error: string | null;
}

export function useActivePlanning(): ActivePlanning {
  const me = useMe();
  const planningId = planningIdOf(me);
  const loadedId = usePlanningStore((s) => s.planningId);
  const status = usePlanningStore((s) => s.status);
  const error = usePlanningStore((s) => s.error);

  useEffect(() => {
    if (planningId && planningId !== loadedId) void planningStore.load(planningId).catch(() => undefined);
  }, [planningId, loadedId]);

  const current = planningId !== null && planningId === loadedId;
  return { planningId, status: current ? status : planningId ? 'loading' : 'idle', error: current ? error : null };
}

/** Skeleton shaped like a toolbar + table, shown while the planning loads. */
function PlanningSkeleton() {
  return (
    <div className={styles.skeleton} role="status" aria-label="Planning laden…">
      <div className={styles.toolbar}>
        <Skeleton width={160} height={30} />
        <Skeleton width={90} height={30} />
        <Skeleton width={120} height={30} />
      </div>
      {Array.from({ length: 8 }, (_, i) => (
        <div key={i} className={styles.row}>
          <Skeleton width={`${28 + ((i * 17) % 30)}%`} height={14} />
          <Skeleton width={`${18 + ((i * 23) % 40)}%`} height={14} />
        </div>
      ))}
    </div>
  );
}

/**
 * Skeleton while the planning loads for the first time; afterwards the children
 * stay mounted, also while it is re-fetched in the background (the store keeps
 * status 'ready' and sets `reloading`, e.g. for "Toon historie").
 */
export function ActivePlanningGate({ children, fallback }: { children: ReactNode; fallback?: ReactNode }) {
  const { planningId, status, error } = useActivePlanning();
  const failed = planningId !== null && status === 'error';
  // A failed load is retried once when a planning view opens again, and whenever the
  // connection comes back, instead of keeping the error until "Opnieuw proberen".
  const failedOnMount = useRef(failed);
  useEffect(() => {
    if (!failed || !planningId) return;
    const retry = () => void planningStore.load(planningId).catch(() => undefined);
    if (failedOnMount.current) {
      failedOnMount.current = false;
      retry();
    }
    window.addEventListener('online', retry);
    return () => window.removeEventListener('online', retry);
  }, [failed, planningId]);
  if (!planningId) return <>{fallback ?? <NoPlanning />}</>;
  if (status === 'error') {
    return (
      <ErrorState
        title="Planning laden mislukt"
        message={error ?? undefined}
        onRetry={() => void planningStore.load(planningId).catch(() => undefined)}
      />
    );
  }
  if (status !== 'ready') return <PlanningSkeleton />;
  return <>{children}</>;
}
